import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared";
import { JobDetail } from "@/components/JobDetail";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PublicPage, PublicHeader, BrandMark } from "@/components/public/PublicShell";
import { RoleCard, RoleView } from "@/components/public/Roles";
import { ArrowLeft, Briefcase, Search, X, FlaskConical, Check, MessageSquare, BadgeCheck, SlidersHorizontal } from "lucide-react";

function SignInLink({ testId }) {
  const nav = useNavigate();
  const { user } = useAuth();
  return <button onClick={() => nav(user ? "/" : "/login")} className="text-sm font-semibold hover:text-primary transition-colors" data-testid={testId}>{user ? "Go to dashboard" : "Sign in"}</button>;
}

const slug = (s) => (s || "").replace(/\s+/g, "-").toLowerCase();
const isRemote = (j) => /remote/i.test(j.location || "");
const plural = (n) => `${n} open role${n === 1 ? "" : "s"}`;
const EMPTY = { location: [], department: [], seniority: [], employment_type: [] };
const GROUPS = [
  { key: "location", label: "Location", id: "loc", pill: false },
  { key: "department", label: "Team", id: "dept", pill: false },
  { key: "seniority", label: "Level", id: "sen", pill: true },
  { key: "employment_type", label: "Type", id: "type", pill: true },
];

function FilterOption({ on, label, count, onClick, pill, testId }) {
  if (pill) return (
    <button onClick={onClick} aria-pressed={on} data-testid={testId}
      className={cn("rounded-full border px-3 py-1 text-[13px] transition-colors", on ? "border-transparent bg-primary/15 text-primary font-medium" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/25")}>{label}</button>
  );
  return (
    <button role="checkbox" aria-checked={on} onClick={onClick} data-testid={testId} className="group w-full flex items-center gap-2.5 py-1.5 text-sm text-left">
      <span className={cn("h-4 w-4 rounded-[5px] border flex items-center justify-center shrink-0 transition-colors", on ? "bg-primary border-primary text-primary-foreground" : "border-foreground/25 group-hover:border-foreground/50")}>{on && <Check className="h-3 w-3" />}</span>
      <span className={cn("flex-1 min-w-0 truncate", on ? "text-foreground" : "text-foreground/80")}>{label}</span>
      {count != null && <span className="hs-mono text-xs text-muted-foreground">{count}</span>}
    </button>
  );
}

function Filters({ jobs, sel, toggle, remoteOnly, setRemoteOnly }) {
  return (
    <div className="space-y-6" data-testid="careers-filters">
      {GROUPS.map((g) => {
        const opts = [...new Set(jobs.map((j) => j[g.key]).filter(Boolean))];
        if (!opts.length) return null;
        return (
          <div key={g.key} className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">{g.label}</p>
            <div className={g.pill ? "flex flex-wrap gap-2" : ""}>
              {opts.map((o) => <FilterOption key={o} pill={g.pill} on={sel[g.key].includes(o)} label={o} count={g.pill ? null : jobs.filter((j) => j[g.key] === o).length} onClick={() => toggle(g.key, o)} testId={`careers-${g.id}-${slug(o)}`} />)}
            </div>
          </div>
        );
      })}
      <FilterOption on={remoteOnly} label="Remote only" count={jobs.filter(isRemote).length} onClick={() => setRemoteOnly((v) => !v)} testId="careers-remote-toggle" />
    </div>
  );
}

export function CareersList() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(EMPTY);
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [sort, setSort] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);

  const load = () => {
    setLoading(true); setErr(null);
    api.get("/public/jobs").then((r) => setJobs(r.data)).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggle = (k, v) => setSel((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));
  const q = query.trim().toLowerCase();
  const filtered = jobs.filter((j) => {
    const mq = !q || [j.title, j.summary, j.department, j.location, j.company_name].some((v) => (v || "").toLowerCase().includes(q));
    return mq && GROUPS.every((g) => !sel[g.key].length || sel[g.key].includes(j[g.key])) && (!remoteOnly || isRemote(j));
  }).sort((a, b) => (sort === "az" ? (a.title || "").localeCompare(b.title || "") : new Date(b.published_at || b.created_at || 0) - new Date(a.published_at || a.created_at || 0)));
  const chips = [...GROUPS.flatMap((g) => sel[g.key].map((v) => ({ k: g.key, v }))), ...(remoteOnly ? [{ k: "remote", v: "Remote only" }] : [])];
  const activeFilters = chips.length + (q ? 1 : 0);
  const clearAll = () => { setQuery(""); setSel(EMPTY); setRemoteOnly(false); setSort("newest"); };

  return (
    <PublicPage data-testid="careers-page">
      <PublicHeader right={<>
        <SignInLink testId="careers-signin" />
        <Button size="sm" className="rounded-full px-4 font-semibold hidden sm:inline-flex" onClick={() => nav("/onboarding")} data-testid="careers-upload-cv">Upload CV to see your fit</Button>
      </>}><BrandMark testId="careers-brand" /></PublicHeader>

      <section className="relative overflow-hidden border-b border-border/70" data-testid="careers-hero">
        <img src="/omani_employer.jpg" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover object-[50%_35%] opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/30" />
        <div className="relative mx-auto max-w-[1360px] px-4 sm:px-8 py-12 sm:py-16 short:py-10 space-y-3">
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight" data-testid="careers-title">Open roles</h1>
          <p className="text-base sm:text-lg text-foreground/80 max-w-xl">{loading ? "Loading roles…" : `${plural(jobs.length)}. Every application is reviewed by a person.`}</p>
        </div>
      </section>

      <div className="mx-auto max-w-[1360px] px-4 sm:px-8 lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-10">
        <aside className={cn("lg:block lg:sticky lg:top-16 lg:self-start lg:max-h-[calc(100dvh-4rem)] lg:overflow-y-auto lg:border-r lg:border-border/70 lg:pr-8 pt-6 lg:pb-6", !showFilters && "hidden")} aria-label="Filter roles">
          <Filters jobs={jobs} sel={sel} toggle={toggle} remoteOnly={remoteOnly} setRemoteOnly={setRemoteOnly} />
        </aside>
        <main className="py-6 space-y-4 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search roles, teams, companies, locations" aria-label="Search roles" data-testid="careers-search"
                className="w-full h-11 rounded-xl bg-card border border-border pl-10 pr-10 text-sm outline-none focus:border-primary/60 transition-colors" />
              {query && <button onClick={() => setQuery("")} aria-label="Clear search" data-testid="careers-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
            </div>
            <Button variant="outline" className="lg:hidden h-11 gap-2" onClick={() => setShowFilters((v) => !v)} aria-expanded={showFilters} data-testid="careers-filters-toggle"><SlidersHorizontal className="h-4 w-4" />Filters{chips.length ? ` · ${chips.length}` : ""}</Button>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <span data-testid="careers-count">{loading ? "Open roles" : plural(filtered.length)}</span><span aria-hidden>·</span>
              <label className="flex items-center gap-1">sort by
                <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="careers-sort" className="bg-transparent font-semibold text-foreground outline-none cursor-pointer [&>option]:bg-card">
                  <option value="newest">Newest</option>
                  <option value="az">Title A–Z</option>
                </select>
              </label>
            </div>
          </div>
          {activeFilters > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="careers-active-filters">
              {chips.map((c) => <button key={c.k + c.v} onClick={() => (c.k === "remote" ? setRemoteOnly(false) : toggle(c.k, c.v))} data-testid={`careers-chip-${slug(c.v)}`} className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 py-1 text-[13px] hover:border-foreground/25">{c.v}<X className="h-3 w-3 text-muted-foreground" /></button>)}
              <button onClick={clearAll} data-testid="careers-clear-all" className="text-[13px] font-medium text-primary hover:underline">Clear all</button>
            </div>
          )}
          {loading ? <LoadingState label="Loading open roles…" />
            : err ? <ErrorState message={err} onRetry={load} />
            : filtered.length === 0 ? <EmptyState icon={Briefcase} title={jobs.length === 0 ? "No open roles right now" : "No roles match your filters"} description={jobs.length === 0 ? "Check back soon. New roles are posted regularly." : "Try clearing a filter or searching for something broader."} action={jobs.length > 0 ? <Button variant="outline" className="mt-2" onClick={clearAll} data-testid="careers-empty-clear">Clear filters</Button> : undefined} />
            : <div className="grid sm:grid-cols-2 gap-4" data-testid="careers-grid">{filtered.map((j) => <RoleCard key={j.id} job={j} onOpen={() => nav(`/careers/${j.id}`)} testId={`careers-role-${j.id}`} />)}</div>}
        </main>
      </div>
    </PublicPage>
  );
}

export function CareersPreview() {
  const { token } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [reviewer, setReviewer] = useState("");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/public/preview/${token}`).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || "This preview link is invalid or has expired.")).finally(() => setLoading(false));
  }, [token]);

  const signoff = async (decision) => {
    setSending(true);
    try {
      await api.post(`/public/preview/${token}/signoff`, { decision, comment, reviewer });
      setDone(decision);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not submit your review.");
    } finally { setSending(false); }
  };
  const field = "w-full rounded-lg border border-border bg-background/60 px-3 py-2 text-sm outline-none focus:border-primary";

  return (
    <PublicPage>
      <PublicHeader><BrandMark testId="careers-brand" /></PublicHeader>
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
        {loading ? <LoadingState label="Loading preview…" />
          : err ? <ErrorState message={err} onRetry={() => nav("/careers")} />
          : (
            <div className="space-y-6" data-testid="job-preview">
              <div className="flex items-center gap-2.5 rounded-xl border border-amber-500/30 bg-amber-500/[0.07] px-4 py-3" data-testid="preview-banner">
                <FlaskConical className="h-4 w-4 text-amber-400 shrink-0" />
                <div className="text-sm text-amber-100">
                  {data.status === "published" ? "Live role preview" : "Draft preview"}, shared for internal review.
                  <span className="text-amber-200/70"> This is not the public listing{data.status !== "published" ? " and the role is not yet published" : ""}.</span>
                </div>
              </div>
              <JobDetail job={data.job} variant="recruiter" />
              <div className="rounded-xl border border-border bg-card p-5 space-y-3" data-testid="preview-signoff">
                {done ? (
                  <div className="flex items-center gap-2.5 text-sm" data-testid="signoff-done">
                    <BadgeCheck className="h-5 w-5 text-primary" />
                    <span>Thanks. Your {done === "approved" ? "approval" : "change request"} was sent to the hiring team.</span>
                  </div>
                ) : (
                  <>
                    <div className="text-sm font-semibold">Sign off on this draft</div>
                    <p className="text-xs text-muted-foreground">Give the recruiter a quick decision. They'll be notified with your name and note.</p>
                    <input value={reviewer} onChange={(e) => setReviewer(e.target.value)} placeholder="Your name" data-testid="signoff-name" className={field} />
                    <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Optional comments or requested changes…" data-testid="signoff-comment" className={cn(field, "resize-none")} />
                    <div className="flex flex-wrap gap-2">
                      <Button onClick={() => signoff("approved")} disabled={sending} data-testid="signoff-approve" className="gap-1.5"><Check className="h-4 w-4" /> Approve</Button>
                      <Button variant="outline" onClick={() => signoff("changes")} disabled={sending} data-testid="signoff-changes" className="gap-1.5"><MessageSquare className="h-4 w-4" /> Request changes</Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
      </div>
    </PublicPage>
  );
}

export function CareersJob() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/public/jobs/${jobId}`).then((r) => setJob(r.data)).catch((e) => setErr(e.response?.status === 404 ? "This role is no longer open." : e.message)).finally(() => setLoading(false));
  }, [jobId]);

  const apply = () => (user?.role === "candidate" ? nav(`/candidate/apply/${jobId}`) : nav("/login"));

  return (
    <PublicPage data-testid="careers-job-page">
      <PublicHeader right={<SignInLink testId="careers-signin" />}>
        <BrandMark testId="careers-brand" />
        <Link to="/careers" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5" data-testid="careers-back"><ArrowLeft className="h-4 w-4" />All roles</Link>
      </PublicHeader>
      <main className="mx-auto max-w-[1200px] px-4 sm:px-8 py-10 sm:py-12">
        {loading ? <LoadingState label="Loading role…" />
          : err ? <ErrorState message={err} onRetry={() => nav("/careers")} />
          : <RoleView job={job} user={user} onApply={apply} applyTestId="careers-apply" />}
      </main>
    </PublicPage>
  );
}
